import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { CostChartViewMode } from '../costChartTypes';

type CostChartHeaderProps = {
  title: string;
  navLabel: string;
  previousLabel: string;
  nextLabel: string;
  rangeLabel: string;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  mode: CostChartViewMode;
  onModeChange: (mode: CostChartViewMode) => void;
  dailyLabel: string;
  monthlyLabel: string;
  viewToggleLabel: string;
  hasMonthlyCostLimit: boolean;
  hasYearlyCostLimit: boolean;
  showMonthlyLimit: boolean;
  showYearlyLimit: boolean;
  onShowMonthlyLimitChange: (checked: boolean) => void;
  onShowYearlyLimitChange: (checked: boolean) => void;
  monthlyLimitLabel: string;
  yearlyLimitLabel: string;
  limitTogglesLabel: string;
};

export function CostChartHeader({
  title,
  navLabel,
  previousLabel,
  nextLabel,
  rangeLabel,
  canGoNext,
  onPrev,
  onNext,
  mode,
  onModeChange,
  dailyLabel,
  monthlyLabel,
  viewToggleLabel,
  hasMonthlyCostLimit,
  hasYearlyCostLimit,
  showMonthlyLimit,
  showYearlyLimit,
  onShowMonthlyLimitChange,
  onShowYearlyLimitChange,
  monthlyLimitLabel,
  yearlyLimitLabel,
  limitTogglesLabel,
}: CostChartHeaderProps) {
  return (
    <div className="cost-chart__header">
      <h2 className="cost-chart__title">{title}</h2>
      <nav className="cost-chart__nav" aria-label={navLabel}>
        <button
          type="button"
          className="cost-chart__nav-btn"
          onClick={onPrev}
          aria-label={previousLabel}
        >
          <ChevronLeft size={16} />
        </button>
        <span className="cost-chart__nav-label">{rangeLabel}</span>
        <button
          type="button"
          className="cost-chart__nav-btn"
          onClick={onNext}
          disabled={!canGoNext}
          aria-label={nextLabel}
        >
          <ChevronRight size={16} />
        </button>
      </nav>
      <div className="cost-chart__controls">
        {(hasMonthlyCostLimit ||
          (mode === 'monthly' && hasYearlyCostLimit)) && (
          <div
            className="cost-chart__limit-toggles"
            role="group"
            aria-label={limitTogglesLabel}
          >
            {hasMonthlyCostLimit ? (
              <label className="cost-chart__limit-toggle">
                <input
                  type="checkbox"
                  checked={showMonthlyLimit}
                  onChange={(event) =>
                    onShowMonthlyLimitChange(event.target.checked)
                  }
                />
                <span>{monthlyLimitLabel}</span>
              </label>
            ) : null}
            {mode === 'monthly' && hasYearlyCostLimit ? (
              <label className="cost-chart__limit-toggle">
                <input
                  type="checkbox"
                  checked={showYearlyLimit}
                  onChange={(event) =>
                    onShowYearlyLimitChange(event.target.checked)
                  }
                />
                <span>{yearlyLimitLabel}</span>
              </label>
            ) : null}
          </div>
        )}
        <div
          className="cost-chart__toggle"
          role="group"
          aria-label={viewToggleLabel}
        >
          <button
            type="button"
            className={[
              'cost-chart__toggle-btn',
              mode === 'daily' && 'cost-chart__toggle-btn--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onModeChange('daily')}
            aria-pressed={mode === 'daily'}
          >
            {dailyLabel}
          </button>
          <button
            type="button"
            className={[
              'cost-chart__toggle-btn',
              mode === 'monthly' && 'cost-chart__toggle-btn--active',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onModeChange('monthly')}
            aria-pressed={mode === 'monthly'}
          >
            {monthlyLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
